/**
 * ModelComparison (VH-280)
 *
 * Admin → Model comparison: the results of the comparison runs that decide which current
 * OpenAI model replaces a retiring one, per feature. Runs are started from the command
 * line on vh-sit (`scripts/model-trial.py`), never from here — a run takes minutes and
 * spends money — so this page only reads results and records Reto's blind picks.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../services/api'
import TrialRunCard from './TrialRunCard'
import BlindReview from './BlindReview'

export default function ModelComparison() {
  const { t } = useTranslation()
  const [reviewing, setReviewing] = useState(null)

  const { data: runs = [], isLoading, isError } = useQuery({
    queryKey: ['model-trials'],
    queryFn: async () => (await adminAPI.listModelTrials()).data.runs ?? [],
  })

  return (
    <section className="space-y-4" data-testid="model-comparison">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('admin.modelTrials.title')}</h2>
        <p className="text-sm text-gray-600 mt-1">{t('admin.modelTrials.intro')}</p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
      {isError && <p className="text-sm text-red-600" role="alert">{t('admin.modelTrials.errors.load')}</p>}
      {!isLoading && !isError && runs.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="no-trials">{t('admin.modelTrials.empty')}</p>
      )}

      {reviewing && <BlindReview run={reviewing} onClose={() => setReviewing(null)} />}

      {runs.map((run) => <TrialRunCard key={run.id} run={run} onReview={setReviewing} />)}
    </section>
  )
}
